import { LogCategory } from '@soteria/logger/browser';

import { trackError } from './errorTracking';

import { logger } from './index';

/**
 * Setup global error handlers for the application
 */
export function setupGlobalErrorHandlers() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

    trackError(error, {
      additionalData: {
        type: 'unhandled_promise_rejection',
        reason: event.reason,
      },
    });

    logger.error('Unhandled promise rejection', {
      category: LogCategory.ERROR,
      action: 'unhandled_promise_rejection',
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      reason: event.reason,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
  });

  // Handle uncaught exceptions
  window.addEventListener('error', event => {
    const error = event.error || new Error(event.message);

    trackError(error, {
      additionalData: {
        type: 'uncaught_exception',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });

    logger.error('Uncaught exception', {
      category: LogCategory.ERROR,
      action: 'uncaught_exception',
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
  });

  // Log client-side navigation errors
  const originalPushState = history.pushState;

  history.pushState = function (...args) {
    try {
      return originalPushState.apply(this, args);
    } catch (error) {
      const navigationError = error instanceof Error ? error : new Error(String(error));

      trackError(navigationError, {
        additionalData: {
          type: 'navigation_error',
          navigationArgs: args,
        },
      });

      logger.error('Navigation error', {
        category: LogCategory.ERROR,
        action: 'navigation_error',
        error: {
          name: navigationError.name,
          message: navigationError.message,
          stack: navigationError.stack,
        },
        navigationArgs: JSON.stringify(args),
        timestamp: new Date().toISOString(),
      });

      throw error;
    }
  };

  // Log when the page becomes hidden (user switches tabs, etc.)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      logger.debug('Page became hidden', {
        category: LogCategory.USER,
        action: 'page_hidden',
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
    } else {
      logger.debug('Page became visible', {
        category: LogCategory.USER,
        action: 'page_visible',
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
    }
  });

  // Log when the user is about to leave the page
  window.addEventListener('beforeunload', () => {
    logger.info('Page unload initiated', {
      category: LogCategory.USER,
      action: 'page_beforeunload',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      sessionDuration: performance.now(),
    });
  });

  logger.debug('Global error handlers initialized', {
    category: LogCategory.SYSTEM,
    action: 'global_error_handlers_setup',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Setup performance monitoring
 */
export function setupPerformanceMonitoring() {
  // Monitor long tasks
  if ('PerformanceObserver' in window) {
    const longTaskObserver = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        logger.warn(`Long task detected: ${entry.duration}ms`, {
          category: LogCategory.PERF,
          action: 'long_task',
          duration: entry.duration,
          startTime: entry.startTime,
          timestamp: new Date().toISOString(),
        });
      });
    });

    longTaskObserver.observe({ entryTypes: ['longtask'] });
  }

  // Monitor memory usage if available
  if ('memory' in performance) {
    setInterval(() => {
      const memory = (performance as unknown as { memory: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;

      if (memory.usedJSHeapSize > memory.jsHeapSizeLimit * 0.9) {
        logger.warn('High memory usage detected', {
          category: LogCategory.PERF,
          action: 'high_memory_usage',
          usedJSHeapSize: memory.usedJSHeapSize,
          totalJSHeapSize: memory.totalJSHeapSize,
          jsHeapSizeLimit: memory.jsHeapSizeLimit,
          usagePercentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
          timestamp: new Date().toISOString(),
        });
      }
    }, 30000); // Check every 30 seconds
  }

  logger.debug('Performance monitoring initialized', {
    category: LogCategory.SYSTEM,
    action: 'performance_monitoring_setup',
    timestamp: new Date().toISOString(),
  });
}
